import { Class } from '../models/class.model';
import { FilterItem } from '../models/schedule.model';

export function createRange(start: number, end: number) {
  const items: number[] = [];
  for (let i = start; i <= end; i++) {
    items.push(i);
  }
  return items;
}

export function addDays(date: number | Date, days: number): Date {
  const newDate = new Date(date);
  newDate.setTime(newDate.getTime() + days * 86400000);
  return newDate;
}

type RawEntittyCollectionItem =
  | {
      id: string;
      name: string;
    }
  | {
      id: string;
      name: string;
    }[];

interface RawEntityCollection {
  coach?: RawEntittyCollectionItem;
  course?: RawEntittyCollectionItem;
  room?: RawEntittyCollectionItem;
  trainingCategories?: RawEntittyCollectionItem;
}

type CollectionItemType =
  | 'coach'
  | 'group'
  | 'course'
  | 'room'
  | 'trainingCategories';

export function getUniqueItems(
  collection: RawEntityCollection[],
  itemType: CollectionItemType,
): FilterItem[] {
  return collection
    .map((i) => i[itemType])
    .filter((i) => !!i)
    .map((i) => {
      if (Array.isArray(i)) {
        return i;
      } else {
        return [i];
      }
    })
    .reduce((accum, curr) => {
      accum.push(...curr);
      return accum;
    }, [])
    .filter((i) => i.id)
    .reduce((uniqueItems, i) => {
      !uniqueItems.find((y) => y.id === i.id) &&
        uniqueItems.push({ ...i, ...{ isEnabled: true } });
      return uniqueItems;
    }, [] as FilterItem[]);
}

export function getUniqueLevels(
  collection: RawEntityCollection[],
): FilterItem[] {
  const name = 'level';
  const items: string[] = collection
    .map((x) => x[name])
    .filter((x) => !!x)
    .reduce((uniqueItems, i) => {
      !uniqueItems.find((x) => x === i) && uniqueItems.push(i);
      return uniqueItems;
    }, [] as string[]);
  return items.map((x, index) => ({
    id: '' + (index + 1),
    name: x,
    isEnabled: true,
  }));
}

export function filterSchedule(
  value: Class[],
  groups: FilterItem[],
  courses: FilterItem[],
  coaches: FilterItem[],
  rooms: FilterItem[],
  trainingCategories: FilterItem[],
  levels: FilterItem[],
) {
  const enabledGroups = (groups || []).filter((x) => x.isEnabled);
  const enabledCourses = (courses || []).filter((x) => x.isEnabled);
  const enabledCoaches = (coaches || []).filter((x) => x.isEnabled);
  const enabledRooms = (rooms || []).filter((x) => x.isEnabled);
  const enabledTrainingCategories = (trainingCategories || []).filter(
    (x) => x.isEnabled,
  );
  const enabledLevels = (levels || []).filter((x) => x.isEnabled);

  return value
    .filter(
      (i) =>
        !enabledGroups.length ||
        (i.groupName && enabledGroups.some((x) => i.group.id === x.id)),
    )

    .filter(
      (i) =>
        !enabledCourses.length ||
        (i.course && enabledCourses.some((x) => i.course.id === x.id)),
    )

    .filter(
      (i) =>
        !enabledCoaches.length ||
        (i.coach && enabledCoaches.some((x) => i.coach.id === x.id)) ||
        (i.additionalCoaches &&
          i.additionalCoaches.length &&
          i.additionalCoaches.some((coach) =>
            enabledCoaches.some((x) => coach.id === x.id),
          )),
    )

    .filter(
      (i) =>
        !enabledRooms.length ||
        (i.room && enabledRooms.some((x) => i.room.id === x.id)),
    )

    .filter(
      (i) =>
        !enabledTrainingCategories.length ||
        (i.trainingCategories &&
          enabledTrainingCategories.some((category) =>
            i.trainingCategories.some((x) => x.id === category.id),
          )),
    )

    .filter(
      (i) =>
        !enabledLevels.length ||
        (i.level && enabledLevels.some((x) => i.level === x.name)),
    );
}

export function guid(a: any): any {
  return a
    ? (a ^ ((Math.random() * 16) >> (a / 4))).toString(16)
    : ((1e7).toString(16) + -1e3 + -4e3 + -8e3 + -1e11)
        .replace(/[018]/g, guid)
        .replace(/-/g, '');
}

export function getNumEnding(
  num: number,
  for1: string,
  for4: string,
  for5: string,
) {
  let ending = for5;
  num = num % 100;
  if (num >= 11 && num <= 19) {
    ending = for5;
  } else {
    const i = num % 10;
    switch (i) {
      case 1:
        ending = for1;
        break;

      case 2:
      case 3:
      case 4:
        ending = for4;
        break;

      default:
        ending = for5;
    }
  }
  return ending;
}

export function formatMinutesDurationFull(minutesTotal: number): string {
  if (!Number.isFinite(minutesTotal) || minutesTotal <= 0) {
    return '';
  }

  const hours = Math.floor(minutesTotal / 60);
  const minutes = minutesTotal % 60;
  const parts: string[] = [];

  if (hours > 0) {
    parts.push(
      `${hours}${getNumEnding(hours, ' час', ' часа', ' часов')}`,
    );
  }

  if (minutes > 0) {
    parts.push(
      `${minutes}${getNumEnding(minutes, ' минута', ' минуты', ' минут')}`,
    );
  }

  return parts.join(' ');
}

export function filterItemComparer(
  left: FilterItem,
  right: FilterItem,
): number {
  if (left.name < right.name) {
    return -1;
  }

  if (left.name > right.name) {
    return 1;
  }

  return 0;
}

export function isIOS(): boolean {
  // @ts-ignore
  const userAgent = navigator.userAgent || navigator.vendor || window?.opera;
  // Проверка на iPhone, iPod, iPad
  // @ts-ignore
  return /iPad|iPhone|iPod/.test(userAgent) && !window?.MSStream;
}

export function isTelegramWebViewIOS(): boolean {
  return (
    'TelegramWebviewProxy' in window && 'TelegramWebviewProxyProto' in window
  );
}

export function isTelegramWebViewAndroid(): boolean {
  return 'TelegramWebview' in window;
}

export function prepareFormForExport(form: HTMLFormElement): string {
  const fields = form.querySelectorAll('input, textarea, select');

  fields.forEach((field) => {
    if (field instanceof HTMLInputElement) {
      const input = field;

      switch (input.type) {
        case 'checkbox':
        case 'radio':
          if (input.checked) {
            input.setAttribute('checked', 'true');
          } else {
            input.removeAttribute('checked');
          }
          break;
        default:
          input.setAttribute('value', input.value);
          break;
      }

      if (input.disabled) {
        input.setAttribute('disabled', 'true');
      } else {
        input.removeAttribute('disabled');
      }
    } else if (field instanceof HTMLTextAreaElement) {
      const textarea = field;
      textarea.textContent = textarea.value;

      if (textarea.disabled) {
        textarea.setAttribute('disabled', 'true');
      } else {
        textarea.removeAttribute('disabled');
      }
    } else if (field instanceof HTMLSelectElement) {
      const select = field;

      Array.from(select.options).forEach((option) => {
        if (option.selected) {
          option.setAttribute('selected', 'true');
        } else {
          option.removeAttribute('selected');
        }
      });

      if (select.disabled) {
        select.setAttribute('disabled', 'true');
      } else {
        select.removeAttribute('disabled');
      }
    }
  });

  return form.outerHTML;
}

export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function parseHexColor(color: string): [number, number, number] | null {
  const hex = color.replace('#', '').trim();
  if (!hex) {
    return null;
  }

  if (hex.length === 3 || hex.length === 4) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    if ([r, g, b].some(Number.isNaN)) {
      return null;
    }
    return [r, g, b];
  }

  if (hex.length === 6 || hex.length === 8) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) {
      return null;
    }
    return [r, g, b];
  }

  return null;
}

function parseRgbColor(color: string): [number, number, number] | null {
  const match = color.match(/rgba?\(([^)]+)\)/i);
  if (!match) {
    return null;
  }

  const parts = match[1].split(',').map((x) => x.trim());
  if (parts.length < 3) {
    return null;
  }

  const r = Number(parts[0]);
  const g = Number(parts[1]);
  const b = Number(parts[2]);
  if ([r, g, b].some(Number.isNaN)) {
    return null;
  }

  return [
    Math.max(0, Math.min(255, r)),
    Math.max(0, Math.min(255, g)),
    Math.max(0, Math.min(255, b)),
  ];
}

/**
 * Возвращает контрастный цвет текста для заданного фона.
 * Поддерживает HEX (#RGB, #RGBA, #RRGGBB, #RRGGBBAA) и RGB/RGBA.
 */
export function getContrastTextColor(
  backgroundColor: string | null | undefined,
  lightColor = '#fff',
  darkColor = '#000',
) {
  if (!backgroundColor) return darkColor;

  const color = String(backgroundColor).trim();
  const rgb = color.startsWith('#')
    ? parseHexColor(color)
    : parseRgbColor(color);

  if (!rgb) return darkColor;

  // YIQ-порог 128: ниже — тёмный фон (нужен светлый текст), выше — светлый.
  const [r, g, b] = rgb;
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq < 128 ? lightColor : darkColor;
}

export interface SplitNameParts {
  start: string;
  end: string;
}

/** Разбивает название на две части для «умного» сокращения. */
export function splitNameForDisplay(
  name: string | null | undefined,
): SplitNameParts {
  if (!name?.trim()) {
    return { start: '', end: '' };
  }

  const parts = name.trim().split(/\s+/);
  const splitIndex = parts.length / 2;

  return {
    start: parts.slice(0, splitIndex).join(' '),
    end: parts.slice(splitIndex).join(' '),
  };
}
